import * as React from "react";
import * as moment from "moment";

import { Button } from "semantic-ui-react";

import { Appointment } from "@teamprevent/common/calendar/models";

import { getIsMobile, scrollDivTo } from "../browser";

import { Day } from "./Day";
import { CalendarDetails, TimePeriod } from "./models";
import { EntitiesDayViewZoom } from "./Calendar";

export interface EntitiesDayViewProps {
    date: moment.Moment;
    details?: CalendarDetails[];
    highlight?: TimePeriod;
    zoom?: EntitiesDayViewZoom;
    onEntitySelected?: (id: string, name: string) => void;
    onAppointmentClick?: (appointment: Appointment) => void;
    onAppointmentDrop?: (itemId: string, appointment: Appointment) => void;
    onHourDrop?: (itemId: string, hour: moment.Moment, entityId: string) => void;
    onHourClick?: (hour: moment.Moment, entityId: string) => void;
    showAppointmentCount?: boolean;
}

interface EntitiesDayViewState {
    hourWidth: number;
    scrolled: boolean;
}

export class EntitiesDayView extends React.Component<EntitiesDayViewProps, EntitiesDayViewState> {

    private calendarContent: React.RefObject<HTMLDivElement>;

    constructor(props: EntitiesDayViewProps) {
        super(props);
        const hourWidth = this.getHourWidth(this.props.zoom);
        this.state = { hourWidth, scrolled: false };
        this.calendarContent = React.createRef();
    }

    public bookedClinicAppointmentCount = (detail: CalendarDetails) => {
        let clinicAppointmentCount = 0;

        detail.appointments.map(fc => {
            clinicAppointmentCount += fc.appointments.length;
        });

        return clinicAppointmentCount;
    };

    public appointmentCountText = (detail: CalendarDetails) => {
        if (detail.appointments.some(a => {
            return a.clinicType === "Health Surveillance" || a.clinicType === "Open";
        })) {
            return "";
        }

        let clinicAppointmentCount = 0;

        detail.appointments.map(fc => {
            const currentDate = moment(fc.start);
            if (fc.slotLengthInMinutes) {
                while (currentDate < fc.end) {
                    currentDate.add(fc.slotLengthInMinutes, "minutes");
                    if (currentDate <= fc.end) {
                        clinicAppointmentCount += 1;
                    }
                }
            }
        });

        return "/" + clinicAppointmentCount;
    };

    public render() {
        const { date, details, highlight } = this.props;

        const hours: moment.Moment[] = [];
        while (hours.length < 24) {
            hours.push(date.clone().add(hours.length, "hours"));
        }

        return (
            <div className="day-view" >
                <div className="day-headers">
                    <div className="day-header corner-header" />
                    {details.map((detail, index) =>
                        (<div
                            className="day-header"
                            onClick={() =>
                                detail.appointments.length === 1 &&
                                this.props.onAppointmentClick &&
                                this.props.onAppointmentClick(detail.appointments[0])
                            }
                            key={index}
                        >
                            <div className="day-header-text">
                                <span>{detail.entityName}</span>
                                <br />
                                <span className="day-header-sub-heading">{detail.entitySubHeading}</span>
                                {this.props.onEntitySelected && detail.entityId &&
                                    <Button
                                        className="select-entity-button"
                                        as="a"
                                        content="Select"
                                        onClick={() => this.props.onEntitySelected(detail.entityId, detail.entityName)}
                                    />}
                                {this.props.showAppointmentCount &&
                                    <span>{`${this.bookedClinicAppointmentCount(detail)}${this.appointmentCountText(detail)}`}</span>
                                }
                            </div>
                        </div>)
                    )}
                </div>
                <div className="day-content" onScroll={() => this.setScrolled(true)} ref={this.calendarContent} >
                    <div className="hour-labels">
                        {hours.map(hour =>
                            /* eslint-disable-next-line react/forbid-dom-props */
                            <div key={hour.valueOf()} className="hour-label" style={{ width: this.state.hourWidth }}>{hour.format("HH:mm")}</div>
                        )}
                    </div>
                    {details.map((detail, index) =>
                        (<div className="day-row" key={index}>
                            <Day
                                entityId={detail.entityId}
                                timeDirection="horizontal"
                                hourSize={this.state.hourWidth}
                                date={date}
                                appointments={detail.appointments}
                                workingDay={
                                    !detail.workingHours ||
                                    !detail.workingHours.workingDays ||
                                    detail.workingHours.workingDays.indexOf(date.weekday()) !== -1}
                                workingDayStartTime={detail.workingHours && detail.workingHours.workingDayStartTime}
                                workingDayEndTime={detail.workingHours && detail.workingHours.workingDayEndTime}
                                highlight={highlight}
                                onAppointmentClick={this.props.onAppointmentClick}
                                onAppointmentDrop={this.props.onAppointmentDrop}
                                onHourDrop={this.props.onHourDrop}
                                onHourClick={this.props.onHourClick}
                            />
                        </div>)
                    )}
                </div>
            </div>
        );
    }

    public componentDidMount() {
        this.reScroll();
    }

    public componentDidUpdate(prevProps: EntitiesDayViewProps) {
        if (prevProps.zoom !== this.props.zoom) {
            const hourWidth = this.getHourWidth(this.props.zoom);
            this.setScrolled(false);
            this.setState({ hourWidth });
        }
        this.reScroll();
    }

    private reScroll() {
        const domNode = this.calendarContent.current;
        if ((!this.state.scrolled) && domNode && domNode.scrollTo) {
            this.setScrolled(true);
            scrollDivTo(domNode, this.getScrollPosition(this.props.zoom) * this.state.hourWidth);
        }
    }

    private setScrolled = (scrolled: boolean) => this.setState({ scrolled });

    private getHourWidth = (zoom: EntitiesDayViewZoom): number => {
        const isMobile = getIsMobile();
        if (isMobile) {
            return 40;
        }

        let hours = 13;
        const allHoursWidth = this.getAllHoursWidth();
        switch (zoom) {
            case "25%": {
                hours = 24;
                break;
            }
            case "50%": {
                hours = 13;
                break;
            }
            case "75%": {
                hours = 6;
                break;
            }
            case "100%": {
                hours = 3;
                break;
            }
            default: {
                hours = 13;
                break;
            }
        }
        return allHoursWidth / hours;
    };

    private getAllHoursWidth() {
        const fullHDdayViewWidth = 1825;
        const dayHeaders = document.getElementsByClassName("day-headers");
        const dayHeadersWidth = (dayHeaders.length > 0 ? dayHeaders[0].clientWidth : 0);
        const dayView = document.getElementsByClassName("day-view");
        const dayViewWidth = dayView.length > 0 ? dayView[0].clientWidth : fullHDdayViewWidth;
        const maxDayHeaderWidth = 140;
        const allHoursWidth = dayViewWidth - (dayHeadersWidth <= maxDayHeaderWidth ? dayHeadersWidth : maxDayHeaderWidth);
        return allHoursWidth;
    }

    private getScrollPosition(zoom: EntitiesDayViewZoom) {
        switch (zoom) {
            case "25%": {
                // from 00.00 to 23.59
                return 0;
            }
            case "50%": {
                // view of a 12 hours slot
                // from 7:00 to 20.00
                return 7;
            }
            case "75%": {
                //  view of a 6 hours slot
                // from 1 hours before to 5 hours after the current time to the closest hour
                return this.findClosestHour() - 1;
            }
            case "100%": {
                //  view of a 3 hours slot
                //  from 1 hour before to 2 hours after the current time to the closest hour
                return this.findClosestHour() - 1;
            }
            default: {
                return 7;
            }
        }
    }

    private findClosestHour(): number {
        const now = moment();
        const roundUp = moment().minute() >= 30 ? now.add(1, "hour").startOf("hour") : now.startOf("hour");
        return roundUp.hour();
    }

}
